import {
  ApiError,
  applyDiscoveredRules,
  createSourceRule,
  deleteSourceRule,
  listSources,
  patchSource,
  patchSourceRule,
  previewDiscoveredRules,
  syncSource,
  testSourceConnector,
  type DiscoverField,
  type DiscoverProposal,
  type ImportRule,
  type ImportSource,
} from "@/api";
import { DiscoverReview } from "@/components/DiscoverReview";
import { Button } from "@/components/ui/Button";
import { formatRelativeAge } from "@/lib/format";
import { FormEvent, useEffect, useState } from "react";

const EMPTY_CONNECTOR = {
  host: "",
  port: 3306,
  database: "",
  user: "",
  password: "",
  table: "users",
  id_column: "id",
  email_column: "email",
  filters: [
    { column: "bewohnt", equals: "1" },
    { column: "mailings_receive", equals: "1" },
  ],
};

export function SourcesPage() {
  const [sources, setSources] = useState<ImportSource[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ImportRule | "new" | null>(null);
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [idRegexp, setIdRegexp] = useState("");
  const [saving, setSaving] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [proposals, setProposals] = useState<{ items: DiscoverProposal[]; found: number } | null>(null);
  const [applying, setApplying] = useState(false);
  const [connector, setConnector] = useState(EMPTY_CONNECTOR);
  const [connectorSaving, setConnectorSaving] = useState(false);
  const [connectorStatus, setConnectorStatus] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const selected = sources?.find((item) => item.id === selectedId) ?? sources?.[0] ?? null;

  function applySelected(source: ImportSource | null) {
    if (!source) return;
    setEnabled(source.enabled);
    setConnector({
      host: source.config?.host ?? "",
      port: source.config?.port ?? 3306,
      database: source.config?.database ?? "",
      user: source.config?.user ?? "",
      password: "",
      table: source.config?.table ?? "users",
      id_column: source.config?.id_column ?? "id",
      email_column: source.config?.email_column ?? "email",
      filters: (source.config?.filters ?? []).map((item) => ({
        column: item.column,
        equals: String(item.equals ?? "1"),
      })),
    });
    setConnectorStatus(null);
  }

  function load(preferId?: string) {
    listSources()
      .then((items) => {
        setSources(items);
        const next =
          items.find((item) => item.id === (preferId || selectedId)) ??
          items.find((item) => item.default) ??
          items[0] ??
          null;
        setSelectedId(next?.id ?? null);
        applySelected(next);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : "Quellen konnten nicht geladen werden.");
      });
  }

  useEffect(() => {
    load();
  }, []);

  function startCreate() {
    setEditing("new");
    setSlug("");
    setTitle("");
    setIdRegexp("");
    setError(null);
  }

  function startEdit(rule: ImportRule) {
    setEditing(rule);
    setSlug(rule.slug);
    setTitle(rule.title);
    setIdRegexp(rule.id_regexp ?? "");
    setError(null);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setSaving(true);
    setError(null);
    const body = {
      slug: slug.trim(),
      title: title.trim(),
      id_regexp: idRegexp.trim(),
    };
    try {
      if (editing === "new") {
        await createSourceRule(selected.id, body);
      } else if (editing) {
        await patchSourceRule(selected.id, editing.id, body);
      }
      setEditing(null);
      load(selected.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Regel konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(rule: ImportRule) {
    if (!selected) return;
    setError(null);
    try {
      await deleteSourceRule(selected.id, rule.id);
      if (editing && editing !== "new" && editing.id === rule.id) setEditing(null);
      load(selected.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Regel konnte nicht gelöscht werden.");
    }
  }

  async function onDiscover() {
    if (!selected) return;
    setDiscovering(true);
    setError(null);
    try {
      setProposals(await previewDiscoveredRules(selected.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Vorschlag aus der Quelle fehlgeschlagen.");
    } finally {
      setDiscovering(false);
    }
  }

  async function onApplyDiscovered(items: { slug: string; fields: DiscoverField[] }[]) {
    if (!selected) return;
    setApplying(true);
    setError(null);
    try {
      await applyDiscoveredRules(selected.id, items);
      setProposals(null);
      load(selected.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Übernehmen fehlgeschlagen.");
    } finally {
      setApplying(false);
    }
  }

  async function onSaveConnector() {
    if (!selected) return;
    setConnectorSaving(true);
    setError(null);
    setConnectorStatus(null);
    try {
      const next = await patchSource(selected.id, {
        enabled,
        config: {
          driver: "mysql+asyncmy",
          host: connector.host.trim(),
          port: Number(connector.port) || 3306,
          database: connector.database.trim(),
          user: connector.user.trim(),
          table: connector.table.trim() || "users",
          id_column: connector.id_column.trim() || "id",
          email_column: connector.email_column.trim() || "email",
          filters: connector.filters
            .filter((item) => item.column.trim())
            .map((item) => ({ column: item.column.trim(), equals: item.equals })),
          ...(connector.password ? { password: connector.password } : {}),
        },
      });
      setSources((prev) => (prev ? prev.map((item) => (item.id === next.id ? next : item)) : prev));
      applySelected(next);
      setConnectorStatus(next.enabled ? "Connector gespeichert." : "Gespeichert (Quelle ausgeschaltet).");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Connector konnte nicht gespeichert werden.");
    } finally {
      setConnectorSaving(false);
    }
  }

  async function onSyncNow() {
    if (!selected) return;
    setSyncing(true);
    setError(null);
    setConnectorStatus(null);
    try {
      const next = await syncSource(selected.id);
      setSources((prev) => (prev ? prev.map((item) => (item.id === next.id ? next : item)) : prev));
      applySelected(next);
      const n = next.directory?.replica_count ?? 0;
      setConnectorStatus(`Synchronisiert (${n} Zeilen).`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Synchronisieren fehlgeschlagen.");
      load(selected.id);
    } finally {
      setSyncing(false);
    }
  }

  async function onTestConnector() {
    if (!selected) return;
    setConnectorSaving(true);
    setError(null);
    setConnectorStatus(null);
    try {
      const result = await testSourceConnector(selected.id, {
        host: connector.host.trim(),
        port: Number(connector.port) || 3306,
        database: connector.database.trim(),
        user: connector.user.trim(),
        ...(connector.password ? { password: connector.password } : {}),
      });
      setConnectorStatus(result.ok ? "Verbindung ok." : result.detail);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Verbindungstest fehlgeschlagen.");
    } finally {
      setConnectorSaving(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-4">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-base font-semibold">Quellen</h1>
        <Button variant="primary" size="sm" onClick={startCreate} disabled={!selected}>
          Neue Regel
        </Button>
      </div>
      <p className="mb-3 text-sm text-muted">
        Jede Quelle hat ihren eigenen Connector und eigene Regeln. StudNet nimmt bewohnte Nutzer mit
        Mailings; eine Regel filtert die Nutzer-ID per Regexp.
      </p>
      {error ? <p className="mb-3 text-sm text-danger">{error}</p> : null}
      {sources === null && !error ? (
        <div className="h-24 animate-pulse rounded-md bg-surface-subtle" />
      ) : null}

      {sources && sources.length > 0 ? (
        <div className="mb-4 flex flex-wrap gap-2">
          {sources.map((source) => (
            <button
              key={source.id}
              type="button"
              className={`rounded-md border px-2 py-1 text-sm ${
                source.id === selected?.id
                  ? "border-accent bg-accent-dim text-accent"
                  : "border-hairline text-muted hover:text-ink"
              }`}
              onClick={() => {
                setSelectedId(source.id);
                setProposals(null);
                setEditing(null);
                applySelected(source);
              }}
            >
              {source.title}
              {!source.enabled ? " (aus)" : ""}
              {source.default ? " · aktiv" : ""}
            </button>
          ))}
        </div>
      ) : null}

      {selected ? (
        <>
          <p className="mb-3 text-sm text-muted">{selected.hint}</p>

          <section className="mb-4 space-y-2 rounded-lg border border-hairline bg-surface p-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">Empfänger-Replica</h2>
              <Button size="sm" variant="primary" disabled={syncing} onClick={() => void onSyncNow()}>
                {syncing ? "…" : "Jetzt synchronisieren"}
              </Button>
            </div>
            <p className="text-[11px] text-muted">
              Preview und Versand lesen nur diese lokale Kopie. Die Quelldatenbank braucht es nur fürs Sync.
            </p>
            <p className="text-sm">
              {selected.directory?.last_sync_status === "ok" || selected.directory?.replica_count
                ? `${selected.directory.replica_count} Zeilen · Stand ${
                    formatRelativeAge(selected.directory.age_seconds) ?? "unbekannt"
                  }`
                : "Noch nicht synchronisiert."}
              {selected.directory?.stale ? " · älter als 24h" : ""}
              {selected.directory?.filters_drift ? " · Filter geändert — bitte syncen" : ""}
            </p>
            {selected.directory?.last_sync_error ? (
              <p className="text-[11px] text-danger">Letzter Fehler: {selected.directory.last_sync_error}</p>
            ) : null}
          </section>

          {selected.kind === "studnet" ? (
            <section className="mb-4 space-y-3 rounded-lg border border-hairline bg-surface p-4">
              <h2 className="text-sm font-semibold">Datenbank-Connector</h2>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                Quelle aktiv
              </label>
              <div className="grid gap-2 md:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="conn-host">
                    Host
                  </label>
                  <input
                    id="conn-host"
                    className="field font-mono"
                    value={connector.host}
                    onChange={(e) => setConnector((prev) => ({ ...prev, host: e.target.value }))}
                    placeholder="mariadb"
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="conn-port">
                    Port
                  </label>
                  <input
                    id="conn-port"
                    className="field font-mono"
                    type="number"
                    value={connector.port}
                    onChange={(e) => setConnector((prev) => ({ ...prev, port: Number(e.target.value) || 3306 }))}
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="conn-db">
                    Datenbank
                  </label>
                  <input
                    id="conn-db"
                    className="field font-mono"
                    value={connector.database}
                    onChange={(e) => setConnector((prev) => ({ ...prev, database: e.target.value }))}
                    placeholder="radius"
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="conn-user">
                    User
                  </label>
                  <input
                    id="conn-user"
                    className="field font-mono"
                    value={connector.user}
                    onChange={(e) => setConnector((prev) => ({ ...prev, user: e.target.value }))}
                    placeholder="edicta"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="field-label" htmlFor="conn-password">
                    Passwort
                  </label>
                  <input
                    id="conn-password"
                    className="field font-mono"
                    type="password"
                    autoComplete="new-password"
                    value={connector.password}
                    onChange={(e) => setConnector((prev) => ({ ...prev, password: e.target.value }))}
                    placeholder={
                      selected.config?.password_set ? "gesetzt — leer lassen, um zu behalten" : "Passwort"
                    }
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="conn-table">
                    Tabelle
                  </label>
                  <input
                    id="conn-table"
                    className="field font-mono"
                    value={connector.table}
                    onChange={(e) => setConnector((prev) => ({ ...prev, table: e.target.value }))}
                    placeholder="users"
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="conn-id-col">
                    ID-Spalte
                  </label>
                  <input
                    id="conn-id-col"
                    className="field font-mono"
                    value={connector.id_column}
                    onChange={(e) => setConnector((prev) => ({ ...prev, id_column: e.target.value }))}
                    placeholder="id"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="field-label" htmlFor="conn-email-col">
                    E-Mail-Spalte
                  </label>
                  <input
                    id="conn-email-col"
                    className="field font-mono"
                    value={connector.email_column}
                    onChange={(e) => setConnector((prev) => ({ ...prev, email_column: e.target.value }))}
                    placeholder="email"
                  />
                </div>
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <span className="field-label mb-0">Zeilenfilter</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setConnector((prev) => ({
                        ...prev,
                        filters: [...prev.filters, { column: "", equals: "1" }],
                      }))
                    }
                  >
                    Filter hinzufügen
                  </Button>
                </div>
                <p className="mb-2 text-[11px] text-muted">
                  Nur Zeilen, bei denen jede Spalte den Wert hat. Andere Quellen: z. B.{" "}
                  <span className="font-mono">active</span> statt <span className="font-mono">bewohnt</span>.
                  Ohne Filter gelten alle Zeilen.
                </p>
                {connector.filters.length === 0 ? (
                  <p className="text-[11px] text-muted">Keine Filter.</p>
                ) : (
                  <div className="space-y-2">
                    {connector.filters.map((item, index) => (
                      <div key={`${item.column}-${index}`} className="grid grid-cols-[1fr_5rem_auto] gap-2">
                        <input
                          className="field font-mono"
                          value={item.column}
                          placeholder="bewohnt"
                          onChange={(e) => {
                            const value = e.target.value;
                            setConnector((prev) => ({
                              ...prev,
                              filters: prev.filters.map((row, rowIndex) =>
                                rowIndex === index ? { ...row, column: value } : row,
                              ),
                            }));
                          }}
                        />
                        <input
                          className="field font-mono"
                          value={item.equals}
                          placeholder="1"
                          onChange={(e) => {
                            const value = e.target.value;
                            setConnector((prev) => ({
                              ...prev,
                              filters: prev.filters.map((row, rowIndex) =>
                                rowIndex === index ? { ...row, equals: value } : row,
                              ),
                            }));
                          }}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setConnector((prev) => ({
                              ...prev,
                              filters: prev.filters.filter((_, rowIndex) => rowIndex !== index),
                            }))
                          }
                        >
                          Weg
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="primary" size="sm" disabled={connectorSaving} onClick={() => void onSaveConnector()}>
                  {connectorSaving ? "…" : "Connector speichern"}
                </Button>
                <Button size="sm" disabled={connectorSaving} onClick={() => void onTestConnector()}>
                  Verbindung testen
                </Button>
              </div>
              {connectorStatus ? <p className="text-[11px] text-muted">{connectorStatus}</p> : null}
            </section>
          ) : (
            <p className="mb-4 text-sm text-muted">Kein Connector — eingebaute Testdaten.</p>
          )}

          {selected.kind === "studnet" ? (
            <div className="mb-3">
              <Button size="sm" disabled={discovering || proposals !== null} onClick={() => void onDiscover()}>
                {discovering ? "Lese Quelle…" : "Regeln aus der Quelle vorschlagen"}
              </Button>
              <p className="mt-1 text-[11px] text-muted">
                Vergleicht die Regeln mit <span className="font-mono">wohnheime_mailman</span>; du wählst, was
                übernommen wird. Braucht die Quelldatenbank; Versand nicht.
              </p>
            </div>
          ) : null}

          {proposals ? (
            <DiscoverReview
              key={selected.id}
              items={proposals.items}
              found={proposals.found}
              applying={applying}
              onApply={(items) => void onApplyDiscovered(items)}
              onCancel={() => setProposals(null)}
            />
          ) : null}

          {selected.rules.length === 0 ? (
            <p className="mb-3 text-sm text-muted">
              Noch keine Regeln. Ohne Regeln bedeutet „Alle bewohnten“ alle Nutzer dieser Quelle.
            </p>
          ) : (
            <ul className="mb-4 divide-y divide-hairline overflow-hidden rounded-lg border border-hairline bg-surface">
              {selected.rules.map((rule) => (
                <li key={rule.id} className="flex items-center gap-3 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {rule.title}{" "}
                      <span className="font-mono text-[11px] font-normal text-muted">{rule.slug}</span>
                    </div>
                    <div className="truncate font-mono text-[11px] text-muted">
                      {rule.id_regexp ? `/${rule.id_regexp}/` : "kein Filter"}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => startEdit(rule)}>
                    Bearbeiten
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void onDelete(rule)}>
                    Löschen
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}

      {editing ? (
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 rounded-lg border border-hairline bg-surface p-4">
          <h2 className="text-sm font-semibold">{editing === "new" ? "Neue Regel" : "Regel bearbeiten"}</h2>
          <div className="grid gap-2 md:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="rule-slug">
                Kürzel (List-Id)
              </label>
              <input
                id="rule-slug"
                className="field font-mono"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="ferdi1"
                required
              />
            </div>
            <div>
              <label className="field-label" htmlFor="rule-title">
                Titel
              </label>
              <input
                id="rule-title"
                className="field"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ferdinandstrasse 1"
              />
            </div>
          </div>
          <div>
            <label className="field-label" htmlFor="rule-regexp">
              Regexp auf die Nutzer-ID
            </label>
            <input
              id="rule-regexp"
              className="field font-mono"
              value={idRegexp}
              onChange={(e) => setIdRegexp(e.target.value)}
              placeholder="^(?:z40|z41)"
              required
            />
          </div>
          <div className="flex gap-2">
            <Button variant="primary" type="submit" disabled={saving || !slug.trim() || !idRegexp.trim()}>
              {saving ? "Speichern…" : "Speichern"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
              Abbrechen
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
